import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Comlink from 'comlink';
import SearchWorker from 'worker-loader!../../workers/search-worker';
import { setJoinRequest, loadInvitations } from '../../stores/actions/invitations';
import {
	setSelectedEvent, setSelectedMeeting, setOnboardingComplete, setWatcherOnly,
} from '../../stores/actions/session';
import { setShowCalendar, setSelectedCalendarView } from '../../stores/actions/user-interface';
import { calendarOptions } from '../../stores/reducers/user-interface';
import { LoadMeeting } from '../../connection/meeting';
import {
	GetIsLive, GetRequiresApproval, CheckJoinRequest, JoinRequest, CheckWatcherOnly,
} from '../../connection/join-request';
import LoadingSpinner from '../general-components/loading-spinner';
import Colors from '../../utils/colors';
import EventList from './producer/event-list';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import * as Alerts from '../general-components/alert-service';
import Switch from '../general-components/switch';
/* eslint-disable import/no-webpack-loader-syntax */
import CalendarView from './home/calendar-view';
import SelectSmall from '../general-components/select-small';
import { GreenroomState } from '../../stores/types/types';
import useJoin from '../../hooks/use-join';
import useMediaDevices from '../../hooks/use-media-devices';

let searchWorker: any;

(async function init() {
	const worker = new SearchWorker();
	searchWorker = Comlink.wrap(worker) as any;
}());

interface Props {
	connected: boolean;
	totalOnboardingBubbles: number;
	eventsAndShowSessions?: any[];
}

export default function PresenterSelectContent(props: Props) {
	const loadingEvents = useSelector((state: GreenroomState) => state.ContentReducer.loadingEvents);
	const liveChannels = useSelector((state: GreenroomState) => state.ContentReducer.liveChannels);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const selectedEvent = useSelector((state: GreenroomState) => state.SessionReducer.selectedEvent);
	const joinRequest = useSelector((state: GreenroomState) => state.InvitationsReducer.joinRequest);
	const listSortType = useSelector((state: GreenroomState) => state.UserInterfaceReducer.listSortType);
	const listShowPast = useSelector((state: GreenroomState) => state.UserInterfaceReducer.listShowPast);
	const showCalendar = useSelector((state: GreenroomState) => state.UserInterfaceReducer.showCalendar);
	const selectedCalendarView = useSelector((state: GreenroomState) => state.UserInterfaceReducer.selectedCalendarView);

	const dispatch = useDispatch();
	const { joinGreenroom, enterGreenroom } = useJoin();

	const { connected, eventsAndShowSessions } = props;

	const [filteredEvents, setFilteredEvents] = useState<any[]>();
	const [searchString, setSearchString] = useState<string>('');
	const searchDebounce = useRef<NodeJS.Timeout | null>(null);
	const joinRequestCheckInverval = useRef<any>(null);
	const displayedEvents = pastEventFilter()?.length;

	function pastEventFilter() {
		if (listShowPast) {
			return eventsAndShowSessions?.filter((event: any) => new Date(event.start_time) < new Date());
		}
		return eventsAndShowSessions?.filter((event: any) => new Date(event.start_time) >= new Date());
	}

	useEffect(
		() => () => {
			if (joinRequestCheckInverval.current) {
				clearInterval(joinRequestCheckInverval.current);
				dispatch(setJoinRequest(null)); // clear join requests when going to a different page
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
	 [],
	);

	useEffect(() => {
		if (eventsAndShowSessions) {
			searchWorker.sort(listSortType, eventsAndShowSessions).then(async () => {
				setFilteredEvents(await searchWorker.results);
				if (!searchString) setSearchString(await searchWorker.raw);
			});
		}
	}, [listSortType, eventsAndShowSessions]);

	useEffect(() => {
		// forcing the join request to poll AND wait for websocket in case the websocket connection is spotty or slow
		if (joinRequest) joinRequestCheckInverval.current = setInterval(() => checkJoinRequest(joinRequest), 3000);

		return () => {
			if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
		};
	}, [joinRequest]);

	useEffect(() => {
		// producer dropped while awaiting join request
		if (selectedEvent && !liveChannels?.includes(selectedEvent.meeting)) {
			if (selectedEvent.entry_without_producer && joinRequestCheckInverval.current) return;
		}
	}, [liveChannels, selectedEvent]);

	useEffect(() => {
		const updateSelected = eventsAndShowSessions?.find((event: any) => event?.meeting === selectedEvent?.meeting);

		if (updateSelected) {
			setSelectedMeeting({ ...selectedEvent, entry_without_producer: updateSelected.entry_without_producer });
		}
	}, [eventsAndShowSessions]);

	function filterEvents(e: React.KeyboardEvent<HTMLInputElement>) {
		if (!eventsAndShowSessions) {
			return;
		}

		dispatch(setShowCalendar(false));

		const target = e.target as HTMLInputElement;
		const val: string = target.value;

		if (searchDebounce.current) clearTimeout(searchDebounce.current);
		searchDebounce.current = setTimeout(() => {
			searchWorker.search(val, eventsAndShowSessions, listSortType).then(async () => {
				setFilteredEvents(await searchWorker.results);
			});
		}, 250);
	}

	async function checkJoinRequest(joinRequest: number) {
		if (joinRequest) {
			const { approved } = await CheckJoinRequest(token!, selectedEvent.meeting);

			if (approved) {
				if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
				enterGreenroom(selectedEvent);
			}
		}
	}

	useEffect(() => {
		// if they start searching, show list view instead of calendar
		if (searchString && !showCalendar) {
			dispatch(setShowCalendar(false));
		}
	}, [searchString]);

	return user && (
		<div className="select-content">
			<div className="select-content-container">
				{loadingEvents || !filteredEvents ? (
					<div className="loading-container">
						<LoadingSpinner
							height={100}
							width={100}
							color={Colors.red}
						/>
					</div>
				) : (
					<>
						{eventsAndShowSessions && eventsAndShowSessions.length > 0 ? (
							<>
								<div className="select-content-header">
									<h1>
										<span className='margin-right-small'>Shows</span>
										<span>({displayedEvents})</span>
									</h1>
									<div className="select-content-controls">
										{showCalendar && <SelectSmall
											options={calendarOptions}
											onChange={(selectedView) => dispatch(setSelectedCalendarView(selectedView))}
											label={calendarOptions.find((t) => t.value === selectedCalendarView)?.label || calendarOptions[0].label}
											selected={calendarOptions.find((t) => t.value === selectedCalendarView) || calendarOptions[0]}
											dropdown={true}
										/>}
										<div className="search-field">
											<input type="text" onKeyUp={filterEvents} placeholder={'Search'} onChange={(e) => setSearchString(e.target.value)} value={searchString} />
											<span className="search-icon">
												<Icon name={ICONS.SEARCH} color={COLORS.DEFAULT_GRAY} size={14}></Icon>
											</span>
										</div>
										<div className="calendar-switch-container">
											<Switch on={!showCalendar} onChange={() => dispatch(setShowCalendar(!showCalendar))} />
											<span className="calendar-icon">
												<Icon name={ICONS.CALENDAR} color={showCalendar ? COLORS.WHITE : COLORS.GRAY} size={14}></Icon>
											</span>
											<span className="list-icon">
												<Icon name={ICONS.LIST} color={!showCalendar ? COLORS.WHITE : COLORS.GRAY} size={14}></Icon>
											</span>
										</div>
									</div>
								</div>
								{!showCalendar
									? (
										<EventList
											searchString={searchString}
											filteredEvents={filteredEvents}
											connected={connected}
											join={async (meeting) => {
												joinGreenroom(meeting, true);
											}}
											forPresenter={true}
										/>
									) : (
										<CalendarView
											showSessions={eventsAndShowSessions}
											calendarType={selectedCalendarView}
										/>
									)}
							</>
						) : (
							<div className="empty">
								<p>There are no broadcasts scheduled yet</p>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
